import { useEffect, useId, useRef, useState } from 'react';
import {
  PRESS_INGOTS,
  SPEC_FIELD_MAX,
  ZIRCONIA_BLOCKS,
  canEditSpecField,
  type Kit,
  type SpecField,
} from '@/domain';
import { useSetKitSpecification } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { toast } from '@/ui/primitives/toastStore';
import { PencilIcon } from '@/ui/icons';
import styles from './SpecFieldRow.module.css';

const COPY: Record<SpecField, { label: string; ownedBy: string; options: readonly string[] }> = {
  ingot: { label: strings.job.ingot, ownedBy: strings.job.ingotOwnedBy, options: PRESS_INGOTS },
  block: { label: strings.job.block, ownedBy: strings.job.blockOwnedBy, options: ZIRCONIA_BLOCKS },
};

/**
 * The ingot press or the zircon block, on the job view, editable by the bench
 * that chose it. Section 1 of the request of 13 September 2026.
 *
 * In place rather than on a screen of its own: the whole point is that it takes
 * four seconds while holding the tray. The datalist is the one intake already
 * offers, a suggestion list and not a closed set, because a lab that has just
 * opened a box of something new should not wait for a deploy.
 *
 * For anybody the rule does not allow, the row renders exactly as it did before
 * this existed. An empty field that this caller CAN fill reads "Not recorded
 * yet" as a button rather than as an empty row, because an empty row is
 * invisible and the point is that Wax notices it.
 *
 * A manager correcting the field is asked before the new value is saved: the
 * request of 5 October 2026 (third), section 2. The bench that owns it is not,
 * and still saves by looking away. Cancel leaves the stored value: the field
 * has already closed, and opening it again starts from what is stored.
 */
export function SpecFieldRow({ kit, field }: { kit: Kit; field: SpecField }) {
  const inputId = useId();
  const listId = useId();
  const { data: session } = useSession();
  const save = useSetKitSpecification();
  const ask = useConfirmAction();
  const input = useRef<HTMLInputElement>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(kit.specification[field]);

  const { label, ownedBy, options } = COPY[field];
  const value = kit.specification[field];
  const mayEdit =
    session != null && canEditSpecField(field, session.user, session.permissions);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  function open() {
    setDraft(value);
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const next = draft.trim();
    if (next === value) return;
    ask(
      strings.confirm.specField(kit.jobOrderNo, label, next),
      () =>
        save.mutateAsync({ kitId: kit.id, patch: { [field]: next } }).then(
          () => toast(`${label} · ${strings.job.fieldSaved}`),
          // The server's words, whichever refusal it is. A bench told "Wax &
          // Fitting records that" learns the rule; a bench told "failed" does not.
          (error: Error) => toast(failureWords(error)),
        ),
      { title: label },
    );
  }

  if (!mayEdit) {
    return (
      <div className={styles.field}>
        <div className={styles.l}>{label}</div>
        <div className={styles.v}>{value}</div>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className={styles.field}>
        <div className={styles.l}>
          {label} <span className={styles.owner}>{ownedBy}</span>
        </div>
        <button className={styles.open} onClick={open} disabled={save.isPending}>
          <span className={value === '' ? styles.empty : undefined}>
            {value === '' ? strings.job.notRecorded : value}
          </span>
          <PencilIcon />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <label className={styles.l} htmlFor={inputId}>
        {label}
      </label>
      <div className={styles.editing}>
        <input
          id={inputId}
          ref={input}
          className={styles.input}
          list={listId}
          value={draft}
          maxLength={SPEC_FIELD_MAX}
          disabled={save.isPending}
          onChange={(event) => setDraft(event.target.value)}
          // Saving on blur is what makes it four seconds; Escape is the way out
          // that does not save, so a mistyped name is never committed by
          // looking away from it.
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // The key is spent here: it must not go on to press a button of
              // the question that saving may open.
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') setEditing(false);
          }}
        />
        <datalist id={listId}>
          {options.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </div>
    </div>
  );
}
